/* eslint-disable consistent-return */
import cn from 'classnames';
import React, {
  ComponentPropsWithoutRef,
  ElementType,
  isValidElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import CloseIcon from '../SVGIcons/CloseIcon';
import { Body } from './Body';
import classes from './CustomModal.module.scss';
import { CustomModalContext } from './CustomModalContext';
import { Footer } from './Footer';
import { Header } from './Header';

type TRootProps<PW extends ElementType, P extends ElementType = 'div'> = ComponentPropsWithoutRef<'div'> & {
  /** paper wrapper component */
  PaperWrapper?: PW,
  /** props that will be provied to paper wrapper component */
  /** class name that will be provied to paper wrapper component */
  paperWrapperClassName?: string;
  /** paper component */
  Paper?: P,
  /** props that will be provided to paper component */
  paperProps?: ComponentPropsWithoutRef<P>;
  /** class name that will be provided to paper component */
  paperClassName?: string;
  /** defines if modal should be visible */
  isOpen: boolean;
  /** callback when closing modal */
  onClose: () => void;
  /** accepts only Header, Body and Footer components, other children will be ignored */
  children?: ReactNode;
  /** if true then component will be in DOM tree even if isOpen is false */
  keepMounted?: boolean;
  /** if set to modal then when overlow the whole modal will be scrollable,\
   * if set to body then only body will be scrollable */
  scrollMode?: 'modal' | 'body';
}

export function Root<PW extends ElementType = 'div', P extends ElementType = 'div'>(
  {
    paperProps = {} as ComponentPropsWithoutRef<P>,
    paperWrapperClassName,
    paperClassName,
    onClose,
    isOpen,
    children,
    className,
    keepMounted = false,
    scrollMode = 'body',
    ...props
  }: TRootProps<PW, P>,
) {
  const { Paper = 'div', PaperWrapper = 'div' } = props;
  const rootRef = useRef<HTMLDivElement>(null);

  const providerValue = useMemo(
    () => ({
      isOpen,
      rootRef,
    }),
    [isOpen],
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const handleEscButton = (e: KeyboardEvent) => {
        if (e.code === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEscButton);
      return () => document.removeEventListener('keydown', handleEscButton);
    }
  }, [isOpen, onClose]);

  let header: ReactNode | null;
  let body: ReactNode | null;
  let footer: ReactNode | null;

  // assigning transtion and paper from children,
  // any other children will be ignored
  React.Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      if (child.type === Header) {
        header = child;
      }
      if (child.type === Body) {
        body = child;
      }
      if (child.type === Footer) {
        footer = child;
      }
    }
  });

  if (!isOpen && !keepMounted) {
    return null;
  }

  return (
    <CustomModalContext.Provider value={providerValue}>
      <div
        {...props}
        ref={rootRef}
        className={cn(
          classes['custom-modal'],
          {
            [classes['custom-modal--open']]: isOpen,
          },
          classes[`custom-modal--scroll-type-${scrollMode}`],
          className,
        )}
      >
        <div className={classes['custom-modal__mask']} />
        <PaperWrapper
          onClick={onClose}
          className={cn(
            classes['custom-modal__paper-wrapper'],
            paperWrapperClassName,
          )}
        >
          <Paper
            {...paperProps}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
            }}
            className={cn(
              classes['custom-modal__paper'],
              paperProps?.className,
              paperClassName,
            )}
          >
            <button
              type="button"
              onClick={onClose}
              className={classes['custom-modal__close-button']}
            >
              <CloseIcon fill="#1D1D1D" className={classes['custom-modal__close-icon']} />
            </button>
            {header}
            {body}
            {footer}
          </Paper>
        </PaperWrapper>
      </div>
    </CustomModalContext.Provider>
  );
}
